import Component from '@glimmer/component'
import { action } from '@ember/object'
import { service } from '@ember/service'
import type BookLibraryCollection from 'teacher-ui-client/models/book-library/collection'
import type { IntlService } from 'ember-intl'
import type { Log } from '@blakeelearning/log'
import type SessionService from 'teacher-ui-client/services/session'
import type FlashQueueService from 'teacher-ui-client/services/flash-queue'
import type FileDownload from 'teacher-ui-client/services/file-download'
import { not, or, and, gt } from 'ember-truth-helpers'
import { BookLibraryCollectionInlineEdit } from 'teacher-ui-client/components/book-library/collection-inline-edit'
import { BookLibraryCollectionShareCounts } from 'teacher-ui-client/components/book-library/collection-share-counts'
import { t } from 'ember-intl'
import { UiButtonLinkTo } from 'teacher-ui-client/components/ui-button/link-to'
import { UiButton } from 'teacher-ui-client/components/ui-button'
import { ButtonAppearance } from 'teacher-ui-client/components/ui-button/themes'
import { hash } from '@ember/helper'
import { BookLibraryCollectionBooksTable } from 'teacher-ui-client/components/book-library/collection/books-table'
import { on } from '@ember/modifier'
import type { PrimaryFilter } from 'teacher-ui-client/reading-scales/utils'
import type Teacher from 'teacher-ui-client/models/teacher'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'

interface Args {
  collection: BookLibraryCollection
  collections: BookLibraryCollection[]
  primaryFilters: PrimaryFilter[]
}

interface Signature {
  Args: Args
  Element: HTMLElement
}

export class BookLibraryCollectionShow extends Component<Signature> {
  @service flashQueue!: FlashQueueService

  @service intl!: IntlService

  @service log!: Log

  @service session!: SessionService

  @service fileDownload!: FileDownload

  get otherCollections() {
    return this.args.collections.filter(({ id }) => this.args.collection.id !== id)
  }

  get isCollectionOwner() {
    // @ts-expect-error - something wrong with relationship reference types
    return this.session.currentTeacher.id === this.args.collection.belongsTo<Teacher, 'teacher'>('teacher').id()
  }

  @action
  onCollectionRenameSuccess() {
    this.flashQueue.addSuccess({ subtitle: this.intl.t('pages.bookLibrary.collection.show.renameCollection.success') })
  }

  @action
  onCollectionRenameFail(_collection: BookLibraryCollection, errors: any) {
    this.flashQueue.addFail({ subtitle: this.intl.t('pages.bookLibrary.collection.show.renameCollection.fail') })
    this.log.error('book library collection action failed', errors)
  }

  @action
  exportCollectionCsv() {
    const books = this.args.collection.books || []
    const header = [
      this.intl.t('bookLibrary.collection.exportCsv.headers.id'),
      this.intl.t('bookLibrary.collection.exportCsv.headers.bookCode'),
      this.intl.t('bookLibrary.collection.exportCsv.headers.title'),
      this.intl.t('bookLibrary.collection.exportCsv.headers.description'),
    ]
    const rows = books.map((book) => {
      return [book.id, book.code, book.title, book.description]
    })
    const filename = this.intl.t('bookLibrary.collection.exportCsv.filename', {
      date: this.intl.formatDate(new Date()).replace(/\//g, '-'),
      id: this.args.collection.id,
    })

    this.fileDownload.downloadAsCsv([header, ...rows], { filename, header: true })
  }

  <template>
    <section class="space-y-4" ...attributes>
      <div class="grid grid-cols-3">
        <div class="col-start-2 flex flex-col items-center justify-center space-y-2" data-test-collection-title>
          {{#if (or @collection.isFavourite (not this.isCollectionOwner))}}
            <span class="text-2xl">{{@collection.name}}</span>
          {{else}}
            <BookLibraryCollectionInlineEdit
              @collection={{@collection}}
              @prefixIcon="phosphor-note-pencil-fill"
              @buttonIcon="phosphor-check-bold"
              @onSuccess={{this.onCollectionRenameSuccess}}
              @onFail={{this.onCollectionRenameFail}}
            >
              <span class="text-center text-2xl">{{@collection.name}}</span>
            </BookLibraryCollectionInlineEdit>
          {{/if}}
          {{#unless this.isCollectionOwner}}
            <div class="text-center" data-test-collection-owner>
              {{t "pages.bookLibrary.collection.show.createdBy" teacherName=@collection.teacher.fullName}}
            </div>
          {{/unless}}
          <div class="text-dusty-black-200 text-center" data-test-collection-book-count>
            ({{t "bookLibrary.bookCount" count=@collection.books.length}})
          </div>
        </div>
        <div class="flex flex-col items-end space-y-2 justify-self-end">
          {{#if @collection.id}}
            <BookLibraryCollectionShareCounts
              @collectionId={{@collection.id}}
              @hidePendingShareCount={{not this.isCollectionOwner}}
            />
          {{/if}}
          {{#if (and (gt @collection.books.length 0) this.isCollectionOwner)}}
            <UiButtonLinkTo
              data-test-share-collection
              aria-label={{t "pages.bookLibrary.collection.show.shareCollection"}}
              @route="subscription-type.book-library.shares.new"
              @query={{hash collectionId=@collection.id}}
              @appearance={{ButtonAppearance.regular}}
            >
              <PhosphorIcon @icon="phosphor-calendar-check-fill" class="mr-1" />
              {{t "pages.bookLibrary.collection.show.shareCollection"}}
            </UiButtonLinkTo>
          {{/if}}
          {{#if (gt @collection.books.length 0)}}
            <UiButton
              class="inline-flex items-center gap-1"
              data-test-export-csv
              {{on "click" this.exportCollectionCsv}}
            >
              <PhosphorIcon @icon="phosphor-file-x-fill" />
              {{t "pages.bookLibrary.collection.show.exportCollection"}}
            </UiButton>
          {{/if}}
        </div>
      </div>
      <BookLibraryCollectionBooksTable
        @collection={{@collection}}
        @otherCollections={{this.otherCollections}}
        @primaryFilters={{@primaryFilters}}
        @isCollectionOwner={{this.isCollectionOwner}}
      />
    </section>
  </template>
}

export default BookLibraryCollectionShow
